import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// Lokale Mini-Helfer (bewusst dupliziert mit Pages — sind 3-Liner und
// hier in der Komponente in sich abgeschlossen).
function fmtNum(n) {
  if (n == null || n === '') return '–';
  const v = parseFloat(n);
  if (isNaN(v)) return '–';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}
function fmtDatum(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE');
}

// ── Drilldown: Verwendungs-Modal (F.1.1, jetzt shared) ────────────────────
// Zeigt alle material_einlagen, die in eine Position eingerechnet wurden.
// Lazy: lädt erst beim Öffnen. Bewusst nur Anzeige — kein Edit/Delete.
// Schließen via X / Outside-Click / ESC.
//
// Wird genutzt von:
//  - Projektfortschritt (gesamt-Projekt, ohne Zeitraum-Filter)
//  - Baustellen-Auswertung (mit Zeitraum-Filter `von` und `bis`)
//
// Props:
//   trigger: { kind: 'position' | 'frei', position?, item?, baustelleId? }
//     - kind 'position' braucht trigger.position (mit id)
//     - kind 'frei' braucht trigger.item (bezeichnung, einheit) + trigger.baustelleId
//   von, bis: optional. Wenn gesetzt, an Endpunkt durchgereicht und im Header
//     als „gefiltert: …" angezeigt.
//   onClose: callback
export default function VerwendungenModal({ trigger, von, bis, onClose }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  // ESC schließt
  useEffect(() => {
    if (!trigger) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [trigger, onClose]);

  // Lazy Fetch beim Öffnen — zusätzlicher Trigger durch Zeitraum-Wechsel
  // wäre selten, daher schlanke Dependency-Liste.
  useEffect(() => {
    if (!trigger) { setItems(null); setErr(''); return; }
    setLoading(true);
    setErr('');
    setItems(null);
    let url;
    const params = new URLSearchParams();
    if (von) params.set('von', von);
    if (bis) params.set('bis', bis);
    if (trigger.kind === 'position') {
      url = `/projekt-positionen/${trigger.position.id}/verwendungen`;
    } else {
      params.set('bezeichnung', trigger.item.bezeichnung || '');
      if (trigger.item.einheit) params.set('einheit', trigger.item.einheit);
      url = `/projektfortschritt/${trigger.baustelleId}/verwendungen-frei`;
    }
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    api.get(url)
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setErr('Konnte Verwendungen nicht laden.'); setItems([]); })
      .finally(() => setLoading(false));
  }, [trigger, von, bis]);

  if (!trigger) return null;

  // Header-Text + Kontext-Zeile je Modus
  const title = trigger.kind === 'position'
    ? (trigger.position.bezeichnung || '–')
    : (trigger.item.bezeichnung || '–');
  const subPosNr = trigger.kind === 'position' ? trigger.position.pos_nr : trigger.item.pos_nr;
  const baseKontext = trigger.kind === 'position'
    ? `Soll: ${fmtNum(trigger.position.sollmenge)} ${trigger.position.einheit || ''} · Ist: ${fmtNum(trigger.position.verbraucht_menge ?? trigger.position.menge)} ${trigger.position.einheit || ''}`
    : `Aggregierte Menge: ${fmtNum(trigger.item.menge_gesamt ?? trigger.item.menge)} ${trigger.item.einheit || ''} · ${trigger.item.verwendungen} Bautagebücher`;
  const zeitraumNote = (von || bis)
    ? ` · gefiltert: ${fmtDatum(von)} – ${fmtDatum(bis)}`
    : '';
  const kontext = baseKontext + zeitraumNote;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 20,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 960, width: '100%', margin: 0,
          maxHeight: '90vh', overflowY: 'auto', minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
              Verwendungen
              {subPosNr && <span className="text-mono text-copper" style={{ marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>{subPosNr}</span>}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-1)', wordBreak: 'break-word' }}>
              {title}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
              {kontext}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'var(--text-3)', lineHeight: 1, padding: '4px 8px',
              flexShrink: 0,
            }}
          >×</button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-3)' }}>
            <div className="spinner" style={{ margin: '0 auto 10px' }} />
            Wird geladen…
          </div>
        )}

        {err && <div className="alert alert-error">{err}</div>}

        {!loading && items && items.length === 0 && !err && (
          <div className="empty">
            <div className="empty-icon">🔍</div>
            <h3>Keine Verwendungen</h3>
            <p>Für diese Position wurden bisher keine Material-Einträge gebucht{(von || bis) ? ' (im gewählten Zeitraum)' : ''}.</p>
          </div>
        )}

        {!loading && items && items.length > 0 && (
          <div className="table-wrap table-mobile-stack">
            <table>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Bautagebuch</th>
                  <th>Menge</th>
                  <th>Lage</th>
                  <th>Bemerkung</th>
                  <th>Ersteller</th>
                  <th>Bauleiter</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(v => {
                  const statusBadge = v.bautagebuch_status === 'fertig'
                    ? <span className="badge badge-green">Fertig</span>
                    : <span className="badge badge-grey">Entwurf</span>;
                  return (
                    <tr key={v.id}>
                      <td data-label="Datum" className="text-mono" style={{ whiteSpace: 'nowrap' }}>{fmtDatum(v.bautagebuch_datum)}</td>
                      <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                        #{v.bautagebuch_id}
                        <span style={{ marginLeft: 8 }}>{statusBadge}</span>
                      </td>
                      <td data-label="Menge" className="text-mono" style={{ fontWeight: 600 }}>
                        {fmtNum(v.menge)} {v.einheit || ''}
                      </td>
                      <td data-label="Lage" style={{ wordBreak: 'break-word' }}>{v.lage || '–'}</td>
                      <td data-label="Bemerkung" style={{ wordBreak: 'break-word' }}>{v.bemerkung || '–'}</td>
                      <td data-label="Ersteller">{v.ersteller_name || '–'}</td>
                      <td data-label="Bauleiter">{v.bauleiter || '–'}</td>
                      <td data-label="" className="td-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => { onClose(); navigate(`/bautagebuch/${v.bautagebuch_id}`); }}
                          title="Bautagebuch öffnen"
                        >→ Öffnen</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Kleiner Drilldown-Trigger-Button für Tabellenzeilen (Lupen-Icon).
export function DrilldownButton({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || 'Verwendungen ansehen'}
      style={{
        background: 'none', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', cursor: 'pointer',
        padding: '4px 8px', fontSize: 13, color: 'var(--text-2)',
        lineHeight: 1,
      }}
    >🔍</button>
  );
}
